import { Box, Button, Grid, Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Shell } from "../../common/shell/shell.page";
import WavingHandIcon from "@mui/icons-material/WavingHand";
import BarChartIcon from "@mui/icons-material/BarChart";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { useModuleAccess } from "../../settings/module-access";

type TileColor = "primary" | "secondary" | "info" | "success" | "warning" | "error";

type Tile = {
  icon: JSX.Element;
  label: string;
  description: string;
  to: string;
  color: TileColor;
};

export const DashboardPage = () => {
  const theme = useTheme();
  const [user] = application.user.useState();
  const moduleAccess = useModuleAccess();

  const canSell = user?.hasPermission(Permission.SELL) ?? false;
  const canProducts = user?.hasPermission(Permission.WRITE_PRODUCTS) ?? false;
  const canCustomers = user?.hasPermission(Permission.MANAGE_CUSTOMERS) ?? false;
  const canStats = user?.hasPermission(Permission.READ_SALES_ANALYTICS) ?? false;
  const canCash = user?.hasPermission(Permission.CASH_REGISTER_MANAGEMENT) ?? false;
  const canPayable = user?.hasPermission(Permission.ACCOUNTS_PAYABLE) ?? false;
  const isAdmin = user?.hasPermission(Permission.ADMIN) ?? false;

  const tiles: Tile[] = [
    canSell && { icon: <PointOfSaleOutlinedIcon />, label: "Vender", description: "Iniciar ou retomar vendas", to: "/vender", color: "primary" },
    canProducts && { icon: <Inventory2OutlinedIcon />, label: "Produtos", description: "Catálogo e estoque", to: "/produtos", color: "info" },
    canCustomers && { icon: <GroupsIcon />, label: "Clientes", description: "Cadastro e saldos de fiado", to: "/clientes", color: "secondary" },
    canStats && { icon: <BarChartIcon />, label: "Estatísticas", description: "Vendas e relatórios", to: "/vendas/estatisticas", color: "success" },
    canCash && { icon: <AccountBalanceWalletIcon />, label: "Caixa", description: "Abertura e fechamento", to: "/caixa", color: "warning" },
    canPayable && { icon: <ReceiptLongIcon />, label: "Contas a pagar", description: "Despesas e fornecedores", to: "/contas-a-pagar", color: "error" },
    isAdmin && { icon: <PeopleOutlineIcon />, label: "Usuários", description: "Permissões e acessos", to: "/usuarios", color: "secondary" },
    isAdmin && { icon: <SettingsOutlinedIcon />, label: "Configurações", description: "Plano e módulos", to: "/configuracoes", color: "primary" },
  ].filter(Boolean) as Tile[];

  const firstName = user?.name?.split(" ")[0] ?? null;

  return (
    <Shell title="Início">
      <Stack spacing={3}>
        {/* Hero */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            border: 1,
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
            position: "relative",
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -30,
              right: -30,
              opacity: 0.04,
              pointerEvents: "none",
            }}
          >
            <WavingHandIcon sx={{ fontSize: 220 }} />
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: "white",
                flexShrink: 0,
              }}
            >
              <WavingHandIcon sx={{ fontSize: 30 }} />
            </Box>

            <Box flex={1}>
              <Typography variant="h5" fontWeight={800} letterSpacing="-0.02em">
                {firstName ? `Olá, ${firstName}!` : "Bem-vindo de volta!"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Use o menu ou os atalhos abaixo para acessar as áreas do sistema.
              </Typography>
            </Box>

            {canSell && (
              <Button
                variant="contained"
                size="large"
                startIcon={<PointOfSaleOutlinedIcon />}
                component={RouterLink}
                to="/vender"
                sx={{ fontWeight: 800, flexShrink: 0 }}
              >
                Iniciar venda
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Quick access */}
        {tiles.length > 0 && (
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={700}
              sx={{ mb: 1.5 }}
            >
              Acesso rápido
            </Typography>
            <Grid container spacing={1.5}>
              {tiles.map((tile) => (
                <Grid item xs={6} sm={4} md={3} key={tile.to}>
                  <QuickTile {...tile} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Stack>
    </Shell>
  );
};

function QuickTile({ icon, label, description, to, color }: Tile) {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Paper
      elevation={0}
      onClick={() => navigate(to)}
      sx={{
        p: 2,
        height: "100%",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        cursor: "pointer",
        transition: "box-shadow 0.15s, border-color 0.15s",
        userSelect: "none",
        "&:hover": {
          boxShadow: theme.shadows[3],
          borderColor: theme.palette[color].main,
        },
      }}
    >
      <Stack spacing={1.5}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: (t) => alpha(t.palette[color].main, 0.1),
            color: `${color}.main`,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography fontWeight={700} variant="body2">
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {description}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
